import { supabase } from '../lib/supabase';

export interface TokenBalance {
  balance: number;
  lifetime_tokens: number;
}

export interface Transaction {
  id: string;
  amount: number;
  transaction_type: 'purchase' | 'deduction' | 'refund' | 'bonus' | 'expiry';
  description: string;
  created_at: string;
  balance_after: number;
  metadata?: Record<string, any>;
}

export interface CoachingSession {
  id: string;
  title: string;
  business_name: string;
  industry: string;
  stage: string;
  session_summary?: string;
  current_state: any;
  overall_score: number;
  created_at: string;
  updated_at: string;
}

export interface BrainstormSession {
  id: string;
  title: string;
  objective: string;
  ideas: any[]; // stored as jsonb array of Idea objects
  overall_analysis: string | null;
  created_at: string;
  updated_at: string;
}


export interface DashboardStats {
  completedSessions: number;
  averageScore: number;
  daysActive: number;
}

export interface SubscriptionSnapshot {
  planName: 'starter' | 'emprende';
  status: 'active' | 'cancelled' | 'expired' | 'inactive';
  renewalDate: string | null;
  monthlyTokens: number;
  price: number;
  paymentProvider: string | null;
  mercadopagoPreapprovalId: string | null;
}

/**
 * Fetch user's token balance from Supabase
 */
export const fetchTokenBalance = async (userId: string): Promise<TokenBalance | null> => {
  try {
    const { data, error } = await supabase
      .from('token_wallets')
      .select('balance, lifetime_tokens')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching token balance:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Failed to fetch token balance:', error);
    return null;
  }
};

/**
 * Fetch user's transaction history from Supabase
 */
export const fetchTransactionHistory = async (
  userId: string,
  limit: number = 10
): Promise<{ data: Transaction[]; error: string | null }> => {
  try {
    const { data, error } = await supabase
      .from('token_ledger')
      .select('id, amount, transaction_type, description, created_at, balance_after, metadata')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching transaction history:', error);
      return { data: [], error: error.message || 'Error al cargar historial de tokens' };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Failed to fetch transaction history:', error);
    return { data: [], error: 'Error al cargar historial de tokens' };
  }
};

export const fetchSubscriptionSnapshot = async (
  userId: string
): Promise<SubscriptionSnapshot | null> => {
  try {
    const [{ data: profile }, { data: subscription }] = await Promise.all([
      supabase
        .from('user_profiles')
        .select('user_plan')
        .eq('id', userId)
        .maybeSingle(),
      supabase
        .from('subscriptions')
        .select('plan_name, status, current_period_end, payment_provider, updated_at, mercadopago_preapproval_id')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const isEmprende =
      profile?.user_plan === 'emprende' ||
      (subscription?.plan_name === 'emprende' && subscription?.status === 'active');

    const planName: 'emprende' | 'starter' = isEmprende ? 'emprende' : 'starter';

    if (planName === 'starter') {
      return {
        planName,
        status: 'inactive',
        renewalDate: null,
        monthlyTokens: 0,
        price: 0,
        paymentProvider: null,
        mercadopagoPreapprovalId: null,
      };
    }

    const rawStatus = subscription?.status;
    const normalizedStatus =
      rawStatus === 'active'
        ? 'active'
        : rawStatus === 'canceled'
          ? 'cancelled'
          : rawStatus === 'past_due' || rawStatus === 'incomplete'
            ? 'expired'
            : 'active';

    return {
      planName,
      status: normalizedStatus,
      renewalDate: subscription?.current_period_end || null,
      monthlyTokens: 100,
      price: 9900,
      paymentProvider:
        typeof subscription?.payment_provider === 'string' ? subscription.payment_provider : null,
      mercadopagoPreapprovalId:
        typeof subscription?.mercadopago_preapproval_id === 'string'
          ? subscription.mercadopago_preapproval_id
          : null,
    };
  } catch (error) {
    console.error('Failed to fetch subscription snapshot:', error);
    return null;
  }
};

export const fetchBillingActivity = async (
  userId: string,
  limit: number = 8
): Promise<{ data: Transaction[]; error: string | null }> => {
  try {
    // Include 'purchase' (topups) and 'bonus' entries that correspond to real
    // MercadoPago payments (monthly plan grants). Welcome/weekly bonuses are
    // excluded by filtering on metadata fields present only in payment-driven entries.
    const { data, error } = await supabase
      .from('token_ledger')
      .select('id, amount, transaction_type, description, created_at, balance_after, metadata')
      .eq('user_id', userId)
      .in('transaction_type', ['purchase', 'bonus'])
      .order('created_at', { ascending: false })
      .limit(limit * 3); // fetch extra to filter client-side

    if (error) {
      console.error('Error fetching billing activity:', error);
      return { data: [], error: error.message || 'Error al cargar cobros' };
    }

    const rows = (data || []).filter((row) => {
      if (row.transaction_type === 'purchase') return true;
      // For bonus entries, only include those tied to a real payment (have payment_provider in metadata)
      return (
        row.transaction_type === 'bonus' &&
        row.metadata?.payment_provider != null
      );
    });

    return { data: rows.slice(0, limit), error: null };
  } catch (error) {
    console.error('Failed to fetch billing activity:', error);
    return { data: [], error: 'Error al cargar cobros' };
  }
};

/**
 * Fetch user's coaching session history from coaching_sessions table (NEW)
 */
export const fetchCoachingSessions = async (
  userId: string,
  limit: number = 10
): Promise<CoachingSession[]> => {
  try {
    const { data, error } = await supabase
      .from('coaching_sessions')
      .select('id, title, business_name, industry, stage, session_summary, current_state, overall_score, created_at, updated_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching coaching sessions:', error);
      // Fallback to old coaching_submissions table if new table doesn't exist
      return fetchLegacyCoachingSessions(userId, limit);
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch coaching sessions:', error);
    // Fallback to legacy table
    return fetchLegacyCoachingSessions(userId, limit);
  }
};

/**
 * Legacy fallback: Fetch from coaching_submissions table
 */
const fetchLegacyCoachingSessions = async (
  userId: string,
  limit: number = 10
): Promise<CoachingSession[]> => {
  try {
    const { data, error } = await supabase
      .from('coaching_submissions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error || !data) {
      return [];
    }

    // Transform legacy format to new format
    return data.map((session: any) => ({
      id: session.id,
      title: `${session.business_name} — ${session.stage}`,
      business_name: session.business_name,
      industry: session.industry,
      stage: session.stage,
      session_summary: session.session_summary || '',
      current_state: {
        challenge: session.challenge,
        aiResponse: session.ai_response
      },
      overall_score: session.ai_response?.investorReadinessScore || 0,
      created_at: session.created_at,
      updated_at: session.created_at
    }));
  } catch (error) {
    console.error('Failed to fetch legacy coaching sessions:', error);
    return [];
  }
};

/**
 * Calculate dashboard statistics from coaching_sessions table (NEW)
 */
export const calculateDashboardStats = async (userId: string): Promise<DashboardStats> => {
  try {
    const { data: sessions, error } = await supabase
      .from('coaching_sessions')
      .select('created_at, overall_score')
      .eq('user_id', userId);

    if (error || !sessions) {
      // Fallback to legacy table
      return calculateLegacyDashboardStats(userId);
    }

    const completedSessions = sessions.length;

    // Calculate average score from overall_score field
    let totalScore = 0;
    let scoreCount = 0;
    sessions.forEach((session) => {
      if (typeof session.overall_score === 'number') {
        totalScore += session.overall_score;
        scoreCount++;
      }
    });
    const averageScore = scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0;

    // Calculate days active (from first session to now)
    let daysActive = 0;
    if (sessions.length > 0) {
      const firstSessionDate = new Date(sessions[sessions.length - 1].created_at);
      const daysDiff = Math.floor(
        (Date.now() - firstSessionDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      daysActive = Math.max(1, daysDiff);
    }

    return {
      completedSessions,
      averageScore,
      daysActive,
    };
  } catch (error) {
    console.error('Failed to calculate dashboard stats:', error);
    return calculateLegacyDashboardStats(userId);
  }
};

/**
 * Legacy fallback: Calculate stats from coaching_submissions
 */
const calculateLegacyDashboardStats = async (userId: string): Promise<DashboardStats> => {
  try {
    const { data: sessions, error } = await supabase
      .from('coaching_submissions')
      .select('created_at, ai_response')
      .eq('user_id', userId);

    if (error || !sessions) {
      return {
        completedSessions: 0,
        averageScore: 0,
        daysActive: 0,
      };
    }

    const completedSessions = sessions.length;

    let totalScore = 0;
    let scoreCount = 0;
    sessions.forEach((session) => {
      const score = session.ai_response?.investorReadinessScore;
      if (typeof score === 'number') {
        totalScore += score;
        scoreCount++;
      }
    });
    const averageScore = scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0;

    let daysActive = 0;
    if (sessions.length > 0) {
      const firstSessionDate = new Date(sessions[sessions.length - 1].created_at);
      const daysDiff = Math.floor(
        (Date.now() - firstSessionDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      daysActive = Math.max(1, daysDiff);
    }

    return {
      completedSessions,
      averageScore,
      daysActive,
    };
  } catch (error) {
    console.error('Failed to calculate legacy dashboard stats:', error);
    return {
      completedSessions: 0,
      averageScore: 0,
      daysActive: 0,
    };
  }
};

/**
 * Fetch user's brainstorm sessions from brainstorm_sessions table
 */
export const fetchBrainstormSessions = async (
  userId: string,
  limit: number = 25
): Promise<BrainstormSession[]> => {
  try {
    const { data, error } = await supabase
      .from('brainstorm_sessions')
      .select('id, title, objective, ideas, overall_analysis, created_at, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching brainstorm sessions:', error);
      return [];
    }

    return (data || []) as BrainstormSession[];
  } catch (error) {
    console.error('Failed to fetch brainstorm sessions:', error);
    return [];
  }
};

/**
 * Delete a brainstorm session by id (user scoped)
 */
export const deleteBrainstormSession = async (
  brainstormId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('brainstorm_sessions')
      .delete()
      .eq('id', brainstormId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting brainstorm session:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Failed to delete brainstorm session:', error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
};

/**
 * Fetch user's OBX sessions from obx_sessions table
 */
export const fetchOBXSessions = async (
  userId: string,
  limit: number = 25
): Promise<OBXSession[]> => {
  try {
    const { data, error } = await supabase
      .from('obx_sessions')
      .select('id, title, objective, ideas, overall_analysis, created_at, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching OBX sessions:', error);
      return [];
    }

    return (data || []) as OBXSession[];
  } catch (error) {
    console.error('Failed to fetch OBX sessions:', error);
    return [];
  }
};

/**
 * Delete an OBX session by id (user scoped)
 */
export const deleteOBXSession = async (
  obxId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('obx_sessions')
      .delete()
      .eq('id', obxId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting OBX session:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Failed to delete OBX session:', error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
};

export interface OBXSession {
  id: string;
  title: string;
  objective: string;
  ideas: any[];
  overall_analysis?: string;
  created_at: string;
  updated_at: string;
}

export interface AideSession {
  id: string;
  title: string;
  objective: string;
  ideas: any[];
  overall_analysis?: string;
  created_at: string;
  updated_at: string;
  source: 'aide_sessions' | 'coaching_sessions';
}

/**
 * Fetch user's AIde sessions from aide_sessions table
 */
export const fetchAideSessions = async (
  userId: string,
  limit: number = 25
): Promise<AideSession[]> => {
  try {
    const { data, error } = await supabase
      .from('aide_sessions')
      .select('id, title, objective, ideas, overall_analysis, created_at, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching AIde sessions:', error);
      return [];
    }

    const aideSessions = (data || []).map((session) => ({
      ...session,
      source: 'aide_sessions' as const,
    }));

    const { data: coachingData } = await supabase
      .from('coaching_sessions')
      .select('id, title, current_state, created_at, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(limit);

    const coachingSessions = (coachingData || [])
      .filter((session: any) => session?.current_state?.aide?.latest || session?.current_state?.idea_mode)
      .map((session: any) => {
        const aideLatest = session.current_state?.aide?.latest;
        const ideaMode = session.current_state?.idea_mode;
        const fallbackProject = ideaMode?.aide_project;

        return {
          id: session.id,
          title: aideLatest?.title || ideaMode?.title || session.title || 'Idea sin título',
          objective: aideLatest?.objective || ideaMode?.objective || '',
          ideas: aideLatest?.ideas || fallbackProject?.ideas || [],
          overall_analysis: aideLatest?.overall_analysis || null,
          created_at: session.created_at,
          updated_at: session.updated_at,
          source: 'coaching_sessions' as const,
        } as AideSession;
      });

    const combined = [...aideSessions, ...coachingSessions];
    combined.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

    return combined.slice(0, limit);
  } catch (error) {
    console.error('Failed to fetch AIde sessions:', error);
    return [];
  }
};

/**
 * Delete an AIde session by id (user scoped)
 */
export const deleteAideSession = async (
  aideId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('aide_sessions')
      .delete()
      .eq('id', aideId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting AIde session:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Failed to delete AIde session:', error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
};

export const deleteCoachingSession = async (
  sessionId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('coaching_sessions')
      .delete()
      .eq('id', sessionId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting coaching session:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Failed to delete coaching session:', error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
};
